import { useState, useEffect, useMemo } from 'react'
import { Definition } from '../../components/dict/Definition'
import { Puzzle } from '../../components/puzzle/Puzzle'
import { VALID_GUESSES } from '../../constants/validGuesses'
import { isSamePattern, Solution } from '../../lib/words'
import { User } from '../../lib/user'
import { profane } from '../../constants/profane'
import { isOrdinary } from '../../lib/words'

type Props = {
  user: User
  isDarkMode: boolean
  guesses: string[]
  solution: Solution
  onJoinPPlus: () => void
}


const asyncFilter = async <T,>(arr: T[], predicate: (v: T) => Promise<boolean>) => {
	const results = await Promise.all(arr.map(predicate));

	return arr.filter((_v, index) => results[index]);
}
const MAX_PREVIEW = 250
const PREFIXES = ['un', 're', 'dis', 'de', 'pre', 'post', 'ex', 'in', 'im', 'il', 'ir', 'co', 'com', 'con', 'mis', 'over']
const SUFFXIES = ['ing', 'ed', 'ly', 's', 'es', 'ment', 'able', 'ible', 'ful', 'less', 'tion', 'al', 'ize', 'ise', 'ic']

const cipherRot = (str: string, rot: number) => {
  str = str.toUpperCase();
  return str.replace(/[A-Z]/g, rot13);

  function rot13(correspondance: string) {
    const charCode = correspondance.charCodeAt(0);
    //A = 65, Z = 90
    return String.fromCharCode(
            ((charCode + rot) <= 90) ? charCode + rot
                                    : (charCode + rot) % 90 + 64
           );

  }
}

const randCipher = (word: string) => {
  return cipherRot(word, Math.floor(Math.random() * 25) + 1)
}

export const MatchingWords = ({ solution, guesses, isDarkMode, user, onJoinPPlus }: Props) => {
  const [matches, setMatches] = useState<string[]>([])
  const [definitions, setDefinitions] = useState<string[]>(
    Array.from(new Set([solution.word, ...guesses]))
  )

  const addDefinition = (word: string) => {
    if(definitions.includes(word)) {
      return
    }
    setDefinitions(definitions.concat(word))
  }


  useEffect(() => {
    setTimeout(() => {
      const wordList = [...VALID_GUESSES.keys()].filter((w) => w.length === solution.word.length)
      asyncFilter(wordList, async (w) => isSamePattern(w, solution.word) && !profane.has(w))
          .then((m) => {
            setMatches(m)
      })
    }, 50) // delay work so it doesn't block render
  }, [solution.word])

  const ordinaryMatches = useMemo(() => {
    return matches.filter(isOrdinary)
  }, [matches])

  const uncommonMatches = useMemo(() => {
    return matches.filter((w) => !isOrdinary(w))
  }, [matches])

  const groupedByPrefix = useMemo(() => {
    const groups = new Map()
    for (let match of matches) {
      for(let prefix of PREFIXES) {
        if(match.startsWith(prefix)) {
          if(!groups.has(prefix)) {
            groups.set(prefix, [])
          }
          groups.get(prefix).push(match)
        }
      }
    }
    return groups
  }, [matches])

  const groupedBySuffix = useMemo(() => {
    const groups = new Map()
    for (let match of matches) {
      for(let suffix of SUFFXIES) {
        if(match.endsWith(suffix)) {
          if(!groups.has(suffix)) {
            groups.set(suffix, [])
          }
          groups.get(suffix).push(match)
        }
      }
    }
    return groups
  }, [matches])

  const hideMatches = !user.hasPolygonlePlus()
  const definitionsList = (words: string[]) => {
    return <div className="flex flex-wrap items-center justify-center">
      {words.map((m) => <span className="font-bold text-xs mx-2 text-gray-700 dark:text-gray-300" key={m} onClick={() => addDefinition(m)}>{m}</span>)}
    </div>
  }


  return <>
  <div className="flex flex-col text-gray-900 dark:text-gray-100 p-2">
    {matches.length > 0 && 
    <div className="grid md:grid-cols-2 grid-cols-1 dark:bg-slate-900 p-4 rounded mb-2 min-h-[280px]">
      <div className="md:col-span-2 col-span-1 p-2 text-center">{matches.length} word{matches.length === 1 ? '' : 's'} match{matches.length === 1 ? 'es' : ''} this pattern</div>
      <div className="md:col-span-2 col-span-1 p-4">
        <Puzzle isDarkMode={isDarkMode} puzzle={solution.puzzle} size={6} containerSize={8}  />
      </div>
      {hideMatches && 
        <div className="md:col-span-2 col-sppan-1 flex flex-wrap items-center justify-center relative">
          <div className="absolute left-0 right-0 top-0 bottom-0 m-auto flex items-center justify-center">
            <button onClick={onJoinPPlus} className="transform w-[230px] bg-indigo-600 text-center mt-24 text-sm text-white font-semibold py-8 z-10 rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
              Unlock with Polygonle+
            </button>
          </div>

          {Array.from(Array(Math.min(matches.length, MAX_PREVIEW)).keys()).map((n) =>
            <span key={n} className="font-bold text-xs mx-2 blur-sm select-none">{randCipher(solution.word.toLowerCase()).toLowerCase()}</span>
          )}
        </div>
      }
      {!hideMatches && ordinaryMatches.length > 0 &&
      <div>
        <div className="p-2 text-center">{ordinaryMatches.length} ordinary word{ordinaryMatches.length === 1 ? '' : 's'}</div>
        {definitionsList(ordinaryMatches)}
      </div>
      }
      {!hideMatches && uncommonMatches.length > 0 && 
      <div>
        <div className="p-2 text-center">{uncommonMatches.length} uncommon word{uncommonMatches.length === 1 ? '' : 's'}</div>
        {definitionsList(uncommonMatches)}
      </div>
      }
      <div className="flex md:col-span-2 col-span-1 my-8 w-full items-center justify-center">
        <div className="border w-3/4 border-slate-200 dark:border-slate-800"></div>
      </div>
      {!hideMatches && groupedByPrefix.size > 0 &&
      <div>
        <div className="p-2 text-center font-bold">Prefixes</div>
          {[...groupedByPrefix.keys()].map((p: string) => {
            return <div key={p}>
              <div className="mt-2 text-center">{p}-</div>
              {definitionsList(groupedByPrefix.get(p))}
            </div>
          })}
      </div>}
      {!hideMatches && groupedBySuffix.size > 0 && <div>
        <div className="p-2 text-center font-bold">Suffixes</div>
        {[...groupedBySuffix.keys()].map((s: string) => {
          return <div key={s}>
            <div className="mt-2 text-center bold">-{s}</div>
            {definitionsList(groupedBySuffix.get(s))}
          </div>
        })}
      </div>}
    </div>
    }
    <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 text-center mb-2">Definitions</h2>
    {definitions.map((d, i: number) => {
      const bgClass = `${i % 2 === 0 ? 'bg-slate-300 dark:bg-slate-700': 'bg-slate-400 dark:bg-slate-800'}`
      return <Definition key={i} word={d} bgClass={bgClass} />
      })}
    <div className="p-4 mt-1">
      <p className="text-xs">Definitions provided by <a className="text-indigo-700 dark:text-indigo-400" href="https://en.wiktionary.org/" target="_blank" rel='noreferrer'>Wiktionary</a> under <a className="text-indigo-700 dark:text-indigo-400" href="https://creativecommons.org/licenses/by-sa/3.0/" target="_blank" rel='noreferrer'>CC BY-SA 3.0</a>.</p>
    </div>
  </div></>
}
